<template>
  <div
    id="latest-itineraries-view"
    style="position: relative;"
    :style="[{ backgroundColor: bgColor }]"
  >
    <template v-if="!loading && itineraries.length <= 0">
      <horizontal-empty-state
        v-if="showSpecificEmptyState"
        entity="itinerary"
        :brandName="brand.name"
        :cityName="$route.query.cityTagName"
      ></horizontal-empty-state>

      <v-card v-else class="pa-5 ma-1" style="height: 100%; max-height: 560px">
        <v-row
          class="ma-0"
          justify="center"
          align="center"
          style="height: 100%;"
        >
          <v-col cols="12" style="max-width: 500px;">
            <p style="text-align: center;" class="mb-0">
              {{ $t('portletNoResultsMsg') }}
            </p>
          </v-col>
        </v-row>
      </v-card>
    </template>

    <template v-else>
      <v-carousel
        v-model="shownItem"
        ref="latestItinerariesCarousel"
        height="auto"
        :cycle="play"
        :interval="interval"
        hide-delimiter-background
        :hide-delimiters="true"
        :show-arrows="false"
        :touchless="true"
      >
        <v-carousel-item
          v-for="(itinerary, index) in itineraries"
          :key="`carousel-item-${index}`"
          eager
          height="100%"
          :disabled="!play && shownItem !== index"
        >
          <div
            v-if="loadedIndexes.includes(index)"
            v-show="index === shownItem"
          >
            <itinerary-item
              :itinerary="itinerary"
              :brandInfo="{ name: brand.name, logo: brand.logo }"
              :headerBtnColor="borderColor"
              :buttonType="buttonType"
              :buttonSize="buttonSize"
              :buttonElevation="buttonElevation"
              :buttonColorText="buttonColorText"
              :itemId="itinerary.itineraryId"
              :portletWidth="portletWidth"
              @redirect="redirectHandler"
              @click="handleCarouselClick"
              @explore-all="exploreAllHandler"
              @pause="play = false"
            ></itinerary-item>
            <!-- @tag-clicked="tagClickedHandler" -->
          </div>
        </v-carousel-item>
      </v-carousel>

      <div class="d-flex align-center justify-center mt-4">
        <v-btn
          @click="previous"
          class="mr-2"
          x-small
          fab
          :color="borderColor"
          height="18px"
          width="18px"
          max-height="18px"
          max-width="18px"
          elevation="0"
          ><v-icon color="white">{{ icons.mdiChevronLeft }}</v-icon></v-btn
        >

        <p class="mb-0">{{ countText }}</p>

        <v-btn
          @click="next"
          class="ml-2"
          x-small
          fab
          :color="borderColor"
          height="18px"
          width="18px"
          max-height="18px"
          max-width="18px"
          elevation="0"
          ><v-icon color="white">{{ icons.mdiChevronRight }}</v-icon></v-btn
        >
      </div>
    </template>
  </div>
</template>

<script>
import uniq from 'lodash/uniq';
const ItineraryItem = () =>
  import('@/components/base_components/BasePortlet/ItineraryItem.vue');
const HorizontalEmptyState = () =>
  import('@/components/base_components/BasePortlet/HorizontalEmptyState.vue');

import { TemplateArrayHelpers } from '@/utilities/mixins/TemplateArrayHelpers';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import { portlet } from '@/utilities/api';
import { mapState, mapActions } from 'vuex';

export default {
  name: 'LatestItineraries',

  metaInfo() {
    return {
      title: this.$t('latestItineraries'),
      meta: [
        {
          name: 'description',
          content: this.metaDescription
        }
      ]
    };
  },

  components: {
    ItineraryItem,
    HorizontalEmptyState
  },

  data: () => ({
    loading: true,
    itineraries: [],
    borderColor: '#fc0384',
    shownItem: 0,
    buttonType: 'tile',
    buttonSize: 'small',
    buttonElevation: 0,
    buttonColorText: '#000000',
    metaDescription: "Project's latest itineraries",
    pageSize: 5,
    ids: [],
    interval: '10000',
    bgColor: '#fff',
    play: true,
    portletWidth: 0,
    showSpecificEmptyState: false,
    loadedIndexes: [0]
  }),

  created() {
    //Set correct language
    if (this.$route?.query?.language) {
      const language = this.$route.query.language;
      if (this.defaultLanguage !== language) {
        localStorage.setItem('langOption', language);

        const payload = {
          code: language
        };

        this.setDefaultLanguage(language);
        this.getTranslatedAppLabels(payload);
      }
    }

    if (this.$route?.query?.color) {
      this.borderColor = `#${this.$route?.query?.color}`;
    } else {
      this.borderColor = this.brand.colors.brand;
    }

    if (this.$route?.query?.bgColor) {
      this.bgColor = `#${this.$route?.query?.bgColor}`;
    }

    if (this.$route?.query?.interval) {
      this.interval = `${this.$route?.query?.interval}000`;
    }

    //button options
    if (this.$route?.query?.buttonType)
      this.buttonType = this.$route?.query?.buttonType;
    if (this.$route?.query?.buttonSize)
      this.buttonSize = this.$route?.query?.buttonSize;
    if (this.$route?.query?.buttonElevation)
      this.buttonElevation = Number(this.$route?.query?.buttonElevation);
    if (this.$route?.query?.buttonColorText)
      this.buttonColorText = `#${this.$route?.query?.buttonColorText}`;

    if (this.$route?.query?.order) {
      this.ids = JSON.parse(this.$route.query.order);
    }

    if (this.$route?.query?.pageSize) {
      this.pageSize = this.$route?.query?.pageSize;
    }

    if (this.$route?.query?.emptyState && this.$route?.query?.cityTagName)
      this.showSpecificEmptyState = true;

    this.fetchItineraries();
  },

  mounted() {
    const portlet = document.getElementById('latest-itineraries-view');
    this.portletWidth = portlet.clientWidth;

    window.addEventListener('resize', () => {
      this.resizeHandler();
    });
  },

  mixins: [TemplateArrayHelpers],

  methods: {
    ...mapActions(['setDefaultLanguage', 'getTranslatedAppLabels']),

    resizeHandler() {
      const portlet = document.getElementById('latest-itineraries-view');
      this.portletWidth = portlet.clientWidth;
    },

    getCityAndState(map) {
      let location = '';

      const itemsToSearch = [
        'locality',
        'administrative_area_level_4',
        'administrative_area_level_3',
        'administrative_area_level_2',
        'administrative_area_level_1'
      ];

      const componentArray = map.address_components;

      if (componentArray.length > 1) {
        for (let i = 0; i < componentArray.length; i++) {
          componentArray[i].types.find(type => {
            if (itemsToSearch.includes(type))
              location = componentArray[i].short_name;
          });

          if (location !== '') break;
        }
      } else {
        componentArray[0].short_name;
      }

      return location;
    },

    async fetchItineraries() {
      try {
        const payload = {
          page: 1,
          pageSize: this.pageSize,
          projectIds: [],
          itineraryIds: [],
          tagIds: [],
          tagNames: [],
          tagFilter: ''
        };

        let queryObjects = Object.keys(this.$route.query);
        const queriesToRemove = [
          'language',
          'color',
          'buttonType',
          'buttonSize',
          'buttonElevation',
          'buttonColorText',
          'orientation',
          'interval',
          'bgColor',
          'emptyState'
        ];

        //remove queries
        queryObjects = queryObjects.filter(
          query => !queriesToRemove.includes(query)
        );

        queryObjects.forEach(query => {
          if (query === 'projectId') {
            if (typeof this.$route.query[query] === 'string') {
              payload.projectIds = [this.$route.query[query]];
            } else {
              payload.projectIds = this.$route.query[query];
            }
          } else if (query === 'tagId') {
            if (typeof this.$route.query[query] === 'string') {
              payload.tagIds.push(this.$route.query[query]);
            } else {
              payload.tagIds = this.$route.query[query];
            }
          } else if (query === 'tagName') {
            if (typeof this.$route.query[query] === 'string') {
              payload.tagNames.push(this.$route.query[query]);
            } else {
              payload.tagNames = this.$route.query[query];
            }
          } else if (query === 'order') {
            Object.keys(this.ids).forEach(key => {
              payload.itineraryIds.push(this.ids[key]);
            });
          } else if (query !== 'tagName' && query.includes('TagName')) {
            //if new filter we do not add AND
            let newTagFilter = payload.tagFilter === '';
            let filter;

            if (typeof this.$route.query[query] === 'string') {
              filter = `(${this.setTagFilter(this.$route.query[query])})`;
            } else {
              let filterHolder = '';
              this.$route.query[query].forEach((tag, index) => {
                if (index === 0) {
                  filterHolder = this.setTagFilter(tag);
                } else {
                  filterHolder += ` OR ${this.setTagFilter(tag)}`;
                }
              });
              filter = `(${filterHolder})`;
            }

            if (newTagFilter) {
              payload.tagFilter = filter;
            } else {
              payload.tagFilter += ` AND ${filter}`;
            }
          } else {
            payload[query] = this.$route.query[query];
          }
        });

        //apply the wrapping ()
        if (payload.tagFilter !== '' && payload.tagFilter.includes('AND')) {
          payload.tagFilter = `(${payload.tagFilter})`;
        }

        const res = await portlet.getLatestItineraries(payload);

        const itineraries = res.list.map(item => {
          const { itinerary, project, itineraryTags, profile } = item;
          const {
            description,
            image,
            itineraryId,
            name,
            pointsOfInterest
          } = itinerary;

          let start = '';
          let end = '';

          if (pointsOfInterest.length > 0) {
            start = this.getCityAndState(pointsOfInterest[0].map);
            end = this.getCityAndState(
              pointsOfInterest[pointsOfInterest.length - 1].map
            );
          }

          const projectName = this.findTranslation(
            project.template[0].name,
            this.defaultLanguage
          );

          return {
            description,
            //fixme remove logo
            logo: image,
            coverImage: image,
            avatarImage: profile.logo,
            itineraryId: itineraryId.replace('#', ''),
            projectId: project.projectId.replace('#', ''),
            profileId: profile.profileId.replace('#', ''),
            name,
            logoType: 'p',
            start,
            end,
            projectName,
            tags: itineraryTags,
            type: 'itinerary'
          };
        });

        const projectNames = uniq(
          this.itineraries.map(itinerary => itinerary.projectName)
        );

        this.metaDescription = `${this.$t('visit')} ${
          this.brand.name
        } ${this.$t('toLearnMoreLatestItineraries')} ${projectNames.join(
          ', '
        )}`;

        this.itineraries = itineraries;
        this.loading = false;
      } catch (e) {
        console.log(e);
      }
    },

    setTagFilter(tag) {
      return `'${tag}' IN tags.name.${this.defaultLanguage}`;
    },

    redirectHandler(itineraryId) {
      // if (confirm(this.redirectMessage)) {
      const routeData = this.$router.resolve({
        name: 'itineraryView',
        params: { id: itineraryId, lang: this.defaultLanguage }
      });
      window.open(routeData.href, '_blank');
      // }
    },

    exploreAllHandler() {
      // if (confirm(this.redirectMessage)) {
      const itineraryIds = this.itineraries.map(
        itinerary => itinerary.itineraryId
      );
      const routeData = this.$router.resolve({
        name: 'browesItineraries',
        query: { itineraryIds }
      });
      window.open(routeData.href, '_blank');
      // }
    },

    next() {
      this.play = true;
      if (this.itineraries.length <= this.shownItem + 1) {
        this.shownItem = 0;
        return;
      }
      this.shownItem++;
    },

    previous() {
      this.play = true;
      if (this.shownItem === 0) {
        this.shownItem = this.itineraries.length - 1;
        return;
      }
      this.shownItem--;
    },

    handleCarouselClick(e) {
      const element = this.$refs.latestItinerariesCarousel.$el;
      const elementWidth = element.clientWidth;
      const elementHeight = element.clientHeight;

      const halfWidth = elementWidth / 2;
      const xPosition = e.clientX;
      const yPosition = e.clientY;

      //if click happens in the bottom 100px do not trigger action
      if (elementHeight - yPosition <= 100) {
        this.play = false;
        return;
      }

      if (xPosition <= halfWidth) {
        this.previous();
      } else {
        this.next();
      }
    },

    tagClickedHandler(tagId) {
      console.log('clicked tagId', tagId);
    }
  },

  computed: {
    ...mapState({
      defaultLanguage: state => state.defaultLanguage,
      brand: state => state.brand.brand
    }),

    icons: () => ({ mdiChevronLeft, mdiChevronRight }),

    countText() {
      return `${this.shownItem + 1} / ${this.itineraries.length}`;
    }

    // redirectMessage() {
    //   return this.$t('redirectFromPortletMsg', {
    //     brandName: this.brand.name
    //   });
    // }
  },

  watch: {
    //keep track of the loaded cards to prevent virtual component deletion
    shownItem(newVal) {
      if (this.loadedIndexes.indexOf(newVal) === -1) {
        this.loadedIndexes.push(newVal);
      }
    }
  }
};
</script>

<style>
div > button.v-carousel__controls__item {
  background-color: var(--v-brand-base);
  opacity: 0.25;
  font-size: 18px !important;
  height: 18px !important;
  width: 18px !important;
}

div > button.v-carousel__controls__item > .v-btn__content > .v-icon {
  background-color: var(--v-brand-base);
  opacity: 0.25;
  font-size: 10px !important;
  height: 10px !important;
  width: 10px !important;
}

div > button.v-carousel__controls__item.v-item--active {
  background-color: var(--v-brand-base);
  opacity: 0.7;
}
</style>
